import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  format,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
} from 'date-fns';

import type { GanttZoomLevel } from '../define.js';

export interface Tick {
  /** Start of the tick bucket, as epoch-ms UTC-ish (date-fns respects local TZ). */
  readonly dateMs: number;
  /** Short label ready for the axis header (not locale-formatted). */
  readonly label: string;
}

/**
 * Returns every tick position between `fromMs` and `toMs` inclusive,
 * aligned to the chosen zoom unit:
 *  - `day`     — every day
 *  - `week`    — every Monday
 *  - `month`   — first of each month
 *  - `quarter` — January/April/July/October 1st
 *
 * Labels are rendered here so consumers don't re-implement formatting
 * per zoom. DE/EN locale is applied by {@link formatTickLabel}; this
 * function returns the canonical (non-localised) label string.
 */
export function getTickPositions(
  fromMs: number,
  toMs: number,
  zoom: GanttZoomLevel
): Tick[] {
  if (toMs <= fromMs) return [];

  const ticks: Tick[] = [];

  switch (zoom) {
    case 'day': {
      // 60 px/day column (PX_PER_DAY_BY_ZOOM.day) — "EEE, MMM d" (~80 px
      // @ 11 px) used to overflow into the next cell. "EEE d" keeps the
      // weekday hint but fits comfortably; month-1st still reads as the
      // boundary because the tick border changes too.
      let d = startOfDay(new Date(fromMs));
      while (d.getTime() <= toMs) {
        ticks.push({ dateMs: d.getTime(), label: format(d, 'EEE d') });
        d = addDays(d, 1);
      }
      break;
    }
    case 'week': {
      let d = startOfWeek(new Date(fromMs), { weekStartsOn: 1 });
      while (d.getTime() <= toMs) {
        ticks.push({ dateMs: d.getTime(), label: format(d, "'KW' II") });
        d = addWeeks(d, 1);
      }
      break;
    }
    case 'month': {
      let d = startOfMonth(new Date(fromMs));
      while (d.getTime() <= toMs) {
        ticks.push({ dateMs: d.getTime(), label: format(d, 'MMM yyyy') });
        d = addMonths(d, 1);
      }
      break;
    }
    case 'quarter': {
      let d = startOfQuarter(new Date(fromMs));
      while (d.getTime() <= toMs) {
        ticks.push({ dateMs: d.getTime(), label: format(d, 'QQQ yyyy') });
        d = addQuarters(d, 1);
      }
      break;
    }
  }

  return ticks;
}
